import { useMemo } from "react"
import { Tooltip } from "antd"
import { Select } from "components/ui/select/Select"
import { defaultTooltipStyles, getShadowTooltipContainer } from "components/ui/customWebviewer/shared/shadowTooltip"
import type { TFurnitureCountertopSeries } from "types/elevationReferences.type"
import style from "./CountertopSeriesSection.module.scss"

const { wrapper, labelRow, label, inheritedTag, selectMuted, resetButton } = style

const INHERITED_TOOLTIP = "The countertop series is inherited from the connected elevation reference."

// The sidebar is user-resizable and can get narrow, so cap the bubble width and let the sentence
// wrap instead of overflowing the panel's edge.
const inheritedTooltipStyles = {
	body: { ...defaultTooltipStyles.body, maxWidth: 220 },
}

type TCountertopSeriesOption = { id: number; name: string }

type Props = {
	options: TCountertopSeriesOption[]
	value: TFurnitureCountertopSeries | null
	// Whether the object is currently connected to an elevation reference. With no eref there is
	// nothing to inherit from, so an explicit series has no "Inherit from elevation" action to offer.
	hasEref: boolean
	onChange: (id: number | null) => void
	onReset: () => void
}

// REQ-405. Same shape as DeliveryDateSection — an "own value" vs. "inherited from elevation" display
// wasn't designed for this field either, so it mirrors that field's existing treatment: a muted
// select + an "Inherited" tag with a tooltip while inherited, an "Inherit from elevation" reset
// action once the object carries its own override.
export const CountertopSeriesSection = ({ options, value, hasEref, onChange, onReset }: Props) => {
	const selectOptions = useMemo(() => options.map((o) => ({ label: o.name, value: o.id })), [options])

	const isInherited = value?.is_inherited ?? false

	return (
		<div className={wrapper}>
			<div className={labelRow}>
				<span className={label}>Countertop series</span>
				{isInherited ? (
					<Tooltip
						title={INHERITED_TOOLTIP}
						placement="top"
						getPopupContainer={getShadowTooltipContainer}
						styles={inheritedTooltipStyles}
					>
						<span className={inheritedTag}>Inherited</span>
					</Tooltip>
				) : null}
			</div>
			<Select
				variant="outlined"
				options={selectOptions}
				value={value?.id ?? null}
				// `onSelect`, not `onChange`: when the value is inherited, the shown id can already
				// equal an option's id, so antd's `onChange` (fires only on an actual value change)
				// would silently no-op on that option. `onSelect` fires on every option click.
				onSelect={(next) => onChange(typeof next === "number" ? next : null)}
				placeholder="Select countertop series"
				className={isInherited ? selectMuted : undefined}
				showSearch
				optionFilterProp="label"
				renderInPlace
			/>
			{!isInherited && value && hasEref ? (
				<button type="button" className={resetButton} onClick={onReset}>
					Inherit from elevation
				</button>
			) : null}
		</div>
	)
}
